/**
 * Post-login "What's new" modal (ZUR OPS).
 *
 *  • Fires once per user per version: while the user hasn't acknowledged the
 *    current APP_VERSION entry it reappears on every load. After "Rozumiem"
 *    we store the acked version in localStorage under `zur-ack-version:<id>`.
 *  • Source of truth for the version is `@/lib/version`; the changelog text is
 *    parsed live from `/docs/CHANGELOG.md` (same file the Help page reads).
 *  • Mounted once in AppLayout so it covers every authenticated route.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, X, Tag, Calendar, ArrowRight,
  Plus, RefreshCw, Bug, CheckCircle2, Shield, Check, Info,
} from 'lucide-react';
import { api } from '@/shared/lib/api';
import { useAuthStore } from '@store/auth.store';

interface LegalPendingResponse { locked: boolean; pending: unknown[] }

type ChangeKind = 'added' | 'changed' | 'fixed' | 'security' | 'kept' | 'note';

interface ChangeItem { kind: ChangeKind; headline: string; detail?: string }
interface ChangelogEntry { version: string; date: string; items: ChangeItem[] }

/** Map English/Polish section words → bucket. */
/** Nazwa sekcji → rodzaj zmiany. Musi znosić obie wersje językowe i obie formy
 *  odmiany („Zmieniono" i „Zmienione") — wpisy w CHANGELOG-u powstają po polsku,
 *  a rozpoznawanie wyłącznie form dokonanych zostawiało modal bez treści (B-169). */
function kindOf(word: string): ChangeKind | null {
  const w = word.toLowerCase();
  // 1.442.0: „Nowe" (synonim „Dodano", jak w Pomocy — HelpPage `bucketOf`); bez niego
  // punkty z „### Nowe" lądowały w „Warto wiedzieć".
  if (/^(added|dodano|dodane|nowe|nowosci|nowości)/.test(w))        return 'added';
  if (/^(changed|zmieniono|zmienione|zmiany)/.test(w))              return 'changed';
  if (/^(fixed|naprawiono|naprawione|poprawki|poprawiono|poprawione|poprawiona)/.test(w)) return 'fixed';
  if (/^(security|bezpieczenstwo|bezpieczeństwo)/.test(w))          return 'security';
  if (/^(kept|zachowane|niezmienione)/.test(w))                     return 'kept';
  if (/^(removed|usuniete|usunięte|deprecated|wycofane)/.test(w))   return 'changed';
  // B-209: „Bez zmian" nazywa dokładnie to, czym jest kubełek kept.
  if (/^bez/.test(w))                                            return 'kept';
  // Rodzina uwag: zastrzeżenia, ograniczenia i rzeczy warte wiedzenia.
  // 1.442.0: nazwa sekcji przychodzi CAŁA (także wielowyrazowa) — „Warto wiedzieć"
  // i „O czym warto wiedzieć" rozpoznajemy wprost; „known" — „Known issues".
  if (/^(warto wiedzie|o czym warto|uwaga|uwagi|note|notes|ważne|wazne|known|ograniczenia|dlaczego)/.test(w)) return 'note';
  return null;
}

/**
 * Parse the top entry of /docs/CHANGELOG.md. Handles the ZUR OPS format:
 *   ## [Admin 1.7.0 + Backend] — 2026-06-07
 *   ### Added — Headline
 *   Paragraph of detail...
 * and also the bullet-list style (### Added\n- item).
 */
function parseLatestEntry(raw: string): ChangelogEntry | null {
  const lines = raw.split(/\r?\n/);
  let i = 0;
  while (i < lines.length && !/^##\s+\[/.test(lines[i])) i++;
  if (i >= lines.length) return null;

  const headerRe = /^##\s+\[[^\]]*?(\d+\.\d+\.\d+)[^\]]*\]\s*[—–-]\s*([\d-]+)/;
  const hm = headerRe.exec(lines[i]);
  if (!hm) return null;
  const [, version, date] = hm;
  i++;

  const items: ChangeItem[] = [];
  let current: ChangeKind | null = null;
  let pendingHeadline: string | null = null;

  const flushParagraph = (text: string) => {
    if (!current) return;
    if (pendingHeadline) {
      items.push({ kind: current, headline: pendingHeadline, detail: text || undefined });
      pendingHeadline = null;
    } else if (text) {
      items.push({ kind: current, headline: text });
    }
  };

  for (; i < lines.length; i++) {
    const l = lines[i];
    if (/^##\s+\[/.test(l) || /^---\s*$/.test(l)) break;

    // 1.442.0: nazwa sekcji może mieć kilka słów („### Warto wiedzieć", „### Bez zmian — celowo").
    // Wcześniej wzorzec łapał jedno słowo, więc wielowyrazowa nazwa nie była rozpoznawana i jej
    // punkty doklejały się do poprzedniej sekcji (np. „Bez zmian w schemacie" jako „Zmieniono").
    // Nagłówek po myślniku ze spacjami („### Dodano — Tytuł") dalej jest tytułem pozycji.
    // Zweryfikowane na całym CHANGELOG-u (650 wydań): 166 wydań zmienia etykiety na właściwe,
    // 0 punktów ginie.
    const sh = /^###\s+(.+?)(?:\s+[—–-]\s+(.+?))?\s*$/.exec(l);
    if (sh) {
      const k = kindOf(sh[1]);
      if (k) {
        // close any prior pending headline that had no detail
        if (pendingHeadline && current) { items.push({ kind: current, headline: pendingHeadline }); pendingHeadline = null; }
        current = k;
        pendingHeadline = sh[2]?.trim() || null;
        continue;
      }
    }
    // B-211: nagłówek sekcji, którego NIE umiemy przypisać do rodzaju zmiany.
    // W praktyce to nazwa ZAKRESU („Backend", „Panel", „Aplikacja instruktora"),
    // wstawiana w środku wydania, żeby pogrupować punkty.
    //
    // Dotąd taka linia spadała niżej i trafiała do okna JAKO POZYCJA o treści
    // „### Backend" — śmieć zamiast informacji. Wielowyrazowe nagłówki nie
    // docierały nawet do rozpoznawania, bo wzorzec wyżej łapie tylko jedno słowo.
    //
    // Teraz taki nagłówek NIE tworzy pozycji i NIE przerywa zbierania: punkty pod
    // nim dalej lądują w rodzaju otwartym wcześniej, a gdy żadnego jeszcze nie było
    // — w „Warto wiedzieć", bo to informacja o wydaniu, nie zmiana.
    if (/^###/.test(l)) {
      if (pendingHeadline && current) { items.push({ kind: current, headline: pendingHeadline }); pendingHeadline = null; }
      if (!current) current = 'note';
      continue;
    }
    if (!current) continue;

    const bm = /^-\s+(.+?)\s*$/.exec(l);
    if (bm) { items.push({ kind: current, headline: bm[1].trim() }); continue; }

    const text = l.trim();
    if (text) flushParagraph(text);
  }
  if (pendingHeadline && current) items.push({ kind: current, headline: pendingHeadline });

  return { version, date, items };
}

/** Render bullet text with **bold** + `code`. */
function MicroMd({ text }: { text: string }) {
  const parts: Array<{ type: 'bold' | 'code' | 'plain'; text: string }> = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push({ type: 'plain', text: text.slice(last, m.index) });
    const t = m[0];
    if (t.startsWith('**')) parts.push({ type: 'bold', text: t.slice(2, -2) });
    else                    parts.push({ type: 'code', text: t.slice(1, -1) });
    last = m.index + t.length;
  }
  if (last < text.length) parts.push({ type: 'plain', text: text.slice(last) });
  return (
    <>
      {parts.map((p, i) =>
        p.type === 'bold' ? <strong key={i} className="text-slate-900">{p.text}</strong>
        : p.type === 'code' ? <code key={i} className="bg-slate-100 text-slate-700 rounded px-1 py-0.5 text-[11px] font-mono">{p.text}</code>
        : <span key={i}>{p.text}</span>,
      )}
    </>
  );
}

const KIND_META: Record<ChangeKind, { label: string; Icon: React.ElementType; color: string }> = {
  added:    { label: 'Dodano',         Icon: Plus,      color: '#10b981' },
  changed:  { label: 'Zmieniono',      Icon: RefreshCw, color: '#d97706' },
  fixed:    { label: 'Naprawiono',     Icon: Bug,       color: '#e11d48' },
  security: { label: 'Bezpieczeństwo', Icon: Shield,    color: '#7c3aed' },
  kept:     { label: 'Bez zmian',      Icon: Check,     color: '#64748b' },
  // B-209: sekcje typu „Uwaga" / „Ważne" / „Ograniczenia" niosą zastrzeżenia do
  // wydania — nie są ani zmianą, ani brakiem zmiany. Bez własnego kubełka wpadały
  // w próżnię (47 takich sekcji w całym pliku), a wciśnięcie ich w „Bez zmian"
  // mówiłoby czytelnikowi coś nieprawdziwego.
  note:     { label: 'Warto wiedzieć', Icon: Info,      color: '#0284c7' },
};

export function ChangelogModal() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [entry, setEntry] = useState<ChangelogEntry | null>(null);
  const [open, setOpen] = useState(false);
  const [wantsToOpen, setWantsToOpen] = useState(false);

  const ackKey = user ? `zur-ack-version:${user.email ?? user.id ?? 'anon'}` : null;

  // Same queryKey as PolicyGate — react-query dedupes the request against its
  // cache instead of firing a second one. Needed because this modal renders
  // at a HIGHER z-index than PolicyGate: without this gate it pops up over
  // the mandatory RODO/ToS acceptance modal, blurring it and swallowing
  // clicks meant for the Accept button underneath (reported as the legal
  // modal looking "cut off" / unusable).
  const { data: legal } = useQuery<LegalPendingResponse>({
    queryKey: ['legal', 'pending'],
    queryFn: () => api.get('/legal/pending') as any,
    enabled: !!user,
    refetchOnWindowFocus: false,
  });
  const legalBlocking = !!legal && (legal.locked || legal.pending.length > 0);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetch('/docs/CHANGELOG.md', { cache: 'no-store' })
      .then((r) => (r.ok ? r.text() : ''))
      .then((text) => {
        if (cancelled) return;
        const e = parseLatestEntry(text);
        setEntry(e);
        if (!e || !ackKey) return;
        try {
          if (localStorage.getItem(ackKey) !== e.version) setWantsToOpen(true);
        } catch { /* private mode */ }
      })
      .catch(() => { /* chip-only fallback */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Actually reveal the modal only once the legal-gate query has resolved
  // and isn't blocking. If the legal gate becomes blocking again later
  // (e.g. an account gets locked mid-session), force-close defensively.
  useEffect(() => {
    if (legalBlocking) { setOpen(false); return; }
    if (wantsToOpen && legal !== undefined) setOpen(true);
  }, [wantsToOpen, legal, legalBlocking]);

  const acknowledge = () => {
    if (ackKey && entry) { try { localStorage.setItem(ackKey, entry.version); } catch { /* ignore */ } }
    setWantsToOpen(false); // permanent dismissal — must not reopen if the shared legal query later refetches
    setOpen(false);
  };

  const counts: Record<ChangeKind, number> = { added: 0, changed: 0, fixed: 0, security: 0, kept: 0, note: 0 };
  entry?.items.forEach((it) => { counts[it.kind]++; });

  return (
    <AnimatePresence>
      {open && entry && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[11000] flex items-center justify-center p-4 bg-slate-900/55 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 8 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 8 }}
            transition={{ type: 'spring', damping: 22, stiffness: 280 }}
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: '85vh' }}
          >
            {/* Header */}
            <div className="relative overflow-hidden flex-shrink-0"
              style={{ background: 'linear-gradient(135deg,#0d4a46,#0d9488 55%,#14b8a6)', padding: '22px 24px 18px' }}>
              <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full bg-white/10" />
              <div className="absolute inset-0 opacity-[0.15]" style={{ background: 'radial-gradient(ellipse at top right, white, transparent)' }} />
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(8px)' }}>
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black text-white leading-tight">Co nowego w ZUR OPS</h2>
                    <p className="text-teal-50/90 text-[11px] mt-0.5">Szybki przegląd ostatnich zmian</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)}
                  title="Zamknij (pojawi się ponownie do czasu potwierdzenia)"
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:bg-white/15"
                  style={{ background: 'rgba(255,255,255,0.12)', color: 'white' }}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Version pill */}
              <div className="relative mt-3 flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white text-teal-700 text-[11px] font-mono font-bold">
                  <Tag className="w-3 h-3" />
                  v{entry.version}
                </div>
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] text-teal-50" style={{ background: 'rgba(255,255,255,0.12)' }}>
                  <Calendar className="w-3 h-3" />
                  {entry.date}
                </div>
                <div className="flex items-center gap-1 text-[10px] text-teal-50">
                  {counts.added   > 0 && <span className="px-2 py-0.5 rounded-full bg-emerald-500/30 text-emerald-50 font-semibold">+{counts.added} nowe</span>}
                  {counts.changed > 0 && <span className="px-2 py-0.5 rounded-full bg-amber-500/30   text-amber-50   font-semibold">↻{counts.changed} zmiany</span>}
                  {counts.fixed   > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-500/30    text-rose-50    font-semibold">✓{counts.fixed} poprawki</span>}
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {entry.items.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-6">Brak szczegółowych notatek dla tej wersji.</p>
              )}
              {entry.items.map((it, idx) => {
                const meta = KIND_META[it.kind];
                return (
                  <div key={idx} className="rounded-xl border border-slate-100 p-3">
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className="w-5 h-5 rounded-lg flex items-center justify-center" style={{ background: `${meta.color}1a` }}>
                        <meta.Icon className="w-3 h-3" style={{ color: meta.color }} />
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                    </div>
                    <p className="text-[13px] font-semibold text-slate-800 leading-snug"><MicroMd text={it.headline} /></p>
                    {it.detail && (
                      <p className="text-[12px] text-slate-600 leading-snug mt-1"><MicroMd text={it.detail} /></p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-100 bg-slate-50/60">
              <button onClick={() => { acknowledge(); navigate('/pomoc#changelog'); }}
                className="flex items-center gap-1 text-[12px] text-teal-700 hover:text-teal-900 font-semibold">
                Zobacz pełną historię zmian
                <ArrowRight className="w-3 h-3" />
              </button>
              <button onClick={acknowledge}
                className="flex items-center gap-1.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 text-xs font-bold transition-colors shadow-sm">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Rozumiem — nie pokazuj ponownie
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
